/**
 * Trigger Type Registry
 *
 * Single source of truth for all trigger types. To add a new trigger type:
 *
 *   1. Add the enum value to TriggerType in model/business-rules.ts
 *   2. Create the form component (if the trigger has configuration)
 *   3. Register it here
 *   4. Add translation keys
 *
 * The trigger wizard reads from this registry — no other files need to change.
 */

import { Type } from '@angular/core';
import { AttributeChangedTriggerSnippetComponent } from '../display-snippet-components/attribute-changed-trigger-snippet/attribute-changed-trigger-snippet.component';
import { TriggerAttributeChangedFormComponent } from '../form-components/trigger-attribute-changed-form/trigger-attribute-changed-form.component';
import { TriggerScheduleFormComponent } from '../form-components/trigger-schedule-form/trigger-schedule-form.component';
import { TriggerSingleAttributeValueFormComponent } from '../form-components/trigger-single-attribute-value-form/trigger-single-attribute-value-form.component';
import { EngageEvent, TriggerType } from '../model';

// ── Types ──────────────────────────────────────────────────────────────────

export type TriggerGroup = 'userManagement' | 'schedule' | 'userActivity';

export interface TriggerGroupConfig {
  key: TriggerGroup;
  labelKey: string;
}

export interface TriggerTypeConfig {
  /** Unique key: for static triggers this is the TriggerType value,
   *  for event triggers it's `UserActivity:${eventName}`. */
  key: string;
  triggerType: TriggerType;
  /** Translation key for button text. If null, computed at registration time. */
  displayTextKey?: string;
  /** Pre-resolved display text (for dynamic event triggers). */
  displayText?: string;
  group: TriggerGroup;
  /** Does this trigger need a configuration form? */
  hasConfig: boolean;
  /** Form component shown when this trigger is selected and hasConfig is true. */
  formComponent?: Type<unknown>;
  /** Snippet component for diagram preview. */
  snippetComponent?: Type<unknown>;
  /** For UserActivity triggers: the event name. */
  userActivity?: string;
  /** For event triggers that need a resource lookup. */
  resourceFieldName?: string;
}

// ── Groups (order matters — determines display order in wizard) ────────────

export const TRIGGER_GROUPS: TriggerGroupConfig[] = [
  { key: 'userManagement', labelKey: 'Automation_UserManagement' },
  { key: 'schedule', labelKey: 'Automation_Trigger_Scheduled' },
  { key: 'userActivity', labelKey: 'Automation_UserActivity' },
];

// ── Registry ───────────────────────────────────────────────────────────────

export const TRIGGER_REGISTRY = new Map<string, TriggerTypeConfig>();

// Static triggers

TRIGGER_REGISTRY.set(TriggerType.UserRecordReceived, {
  key: TriggerType.UserRecordReceived,
  triggerType: TriggerType.UserRecordReceived,
  displayTextKey: 'BusinessRules_ApplyToAllUsers',
  group: 'userManagement',
  hasConfig: false,
});

TRIGGER_REGISTRY.set(TriggerType.UserCreated, {
  key: TriggerType.UserCreated,
  triggerType: TriggerType.UserCreated,
  displayTextKey: 'BusinessRules_ApplyToNewUsers',
  group: 'userManagement',
  hasConfig: false,
});

TRIGGER_REGISTRY.set(TriggerType.UserAttributeChanged, {
  key: TriggerType.UserAttributeChanged,
  triggerType: TriggerType.UserAttributeChanged,
  displayTextKey: 'Automation_UserAttributeChanged',
  group: 'userManagement',
  hasConfig: true,
  formComponent: TriggerAttributeChangedFormComponent,
  snippetComponent: AttributeChangedTriggerSnippetComponent,
});

TRIGGER_REGISTRY.set(TriggerType.Scheduled, {
  key: TriggerType.Scheduled,
  triggerType: TriggerType.Scheduled,
  displayTextKey: 'Automation_Trigger_Scheduled',
  group: 'schedule',
  hasConfig: true,
  formComponent: TriggerScheduleFormComponent,
});

// ── Dynamic registration for event-based triggers ──────────────────────────

/**
 * Called at runtime with the events from the API.
 * Registers a TriggerTypeConfig for each event.
 */
export function registerEventTriggers(
  events: EngageEvent[],
  translate: { instant: (key: string) => string }
): void {
  for (const event of events) {
    const key = `${TriggerType.UserActivity}:${event.eventName}`;
    if (TRIGGER_REGISTRY.has(key)) continue;

    const hasResourceField = !!event.resourceFieldName;
    TRIGGER_REGISTRY.set(key, {
      key,
      triggerType: TriggerType.UserActivity,
      displayText: translate.instant(
        `Automation_Event_${event.eventName.replace(/\s/g, '')}`
      ),
      group: 'userActivity',
      hasConfig: hasResourceField,
      formComponent: hasResourceField
        ? TriggerSingleAttributeValueFormComponent
        : undefined,
      userActivity: event.eventName,
      resourceFieldName: event.resourceFieldName,
    });
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Get all registered triggers grouped by their group key. */
export function getTriggersByGroup(): Map<TriggerGroup, TriggerTypeConfig[]> {
  const grouped = new Map<TriggerGroup, TriggerTypeConfig[]>();
  for (const group of TRIGGER_GROUPS) {
    grouped.set(group.key, []);
  }
  for (const config of TRIGGER_REGISTRY.values()) {
    const list = grouped.get(config.group) ?? [];
    list.push(config);
    grouped.set(config.group, list);
  }
  return grouped;
}

/** Resolve display text for a trigger config entry. */
export function getTriggerDisplayText(
  config: TriggerTypeConfig,
  translate: { instant: (key: string) => string }
): string {
  if (config.displayText) return config.displayText;
  if (config.displayTextKey) return translate.instant(config.displayTextKey);
  return config.key;
}

/** Look up the registry entry for an existing trigger (e.g. when editing). */
export function findTriggerConfig(
  triggerType: TriggerType,
  userActivity?: string
): TriggerTypeConfig | undefined {
  if (userActivity) {
    return TRIGGER_REGISTRY.get(`${TriggerType.UserActivity}:${userActivity}`);
  }
  return TRIGGER_REGISTRY.get(triggerType);
}
